import React from "react";
import { Clock, Mail, Anchor, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { PortsideStripe, PortsideMark } from "@/components/Brand";

export default function PendingApproval() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isRevoked = user?.status === "revoked";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid="pending-page">
      <PortsideStripe />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PortsideMark />
            <span className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400 border-l border-slate-200 pl-3">
              Container Operations
            </span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="pending-logout-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0a2952] px-3 py-1.5 rounded-md hover:bg-slate-100"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 py-12">
        <div className="w-full max-w-xl bg-white border border-slate-200 rounded-md p-8 lg:p-12 text-center">
          <div className={`w-16 h-16 mx-auto mb-6 rounded-full grid place-items-center ${
            isRevoked ? "bg-red-50 text-red-600" : "bg-[#ff5b2c]/10 text-[#ff5b2c]"
          }`}>
            <Clock size={28} />
          </div>

          <div className="overline mb-3">{isRevoked ? "Access revoked" : "Awaiting approval"}</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#0a2952] tracking-tight mb-4">
            {isRevoked ? "Your access has been revoked." : "You're on the waiting list."}
          </h1>
          <p className="text-slate-600 leading-relaxed mb-6">
            {isRevoked ? (
              <>An administrator has revoked your access to the Portside container operations dashboard. If you believe this is a mistake, please reach out to your Portside contact.</>
            ) : (
              <>Thanks for signing in, <span className="font-semibold text-[#0a2952]">{user?.name}</span>. Access to the dashboard is by invitation. An administrator will review your request and let you know.</>
            )}
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <Mail size={16} className="mt-0.5 text-slate-500 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Signed in as</div>
                <div className="text-sm font-semibold text-[#0a2952] truncate" data-testid="pending-email">{user?.email}</div>
              </div>
            </div>
          </div>

          {!isRevoked && (
            <p className="text-xs text-slate-500">
              You'll be granted access without needing to sign in again — just refresh this page after approval.
            </p>
          )}

          <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-center gap-2 text-xs text-slate-400">
            <Anchor size={12} /> Portside · Multipurpose Terminals Ghana Ltd
          </div>
        </div>
      </main>
    </div>
  );
}
